import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useT, useSeo, fmtMoney } from "@/lib/i18n";
import { pub } from "@/lib/api";
import { HaloCard, Eyebrow, CtaLink } from "@/components/bits";
import { useConfig } from "@/components/Layout";
import { ReviewsWall, YouTubeHub, Donate } from "@/components/extras";
import { ThreeCards, ClubCards, Mosaic, SparkPortrait, WeeklyPost } from "@/components/homeExtras";

function Hero() {
  const { t } = useT();
  return (
    <section className="dark-zone relative bg-[var(--forest-deep)] text-[var(--ivory)] overflow-hidden min-h-[640px] md:min-h-[720px] flex items-center">
      <picture>
        <source srcSet="/assets/hero-sanctuary.webp" type="image/webp" />
        <img src="/assets/hero-sanctuary.png" alt="" aria-hidden="true" width="1672" height="941" fetchpriority="high" className="hero-img absolute inset-0 w-full h-full object-cover object-[70%_50%]" />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-r from-[rgba(8,28,25,.92)] via-[rgba(8,28,25,.6)] to-transparent" aria-hidden="true" />
      <div className="wrap relative py-24 reveal">
        <Eyebrow>{t("heroEyebrow")}</Eyebrow>
        <h1 className="max-w-[14ch] text-[var(--ivory)]" data-testid="hero-title">{t("heroH1")}</h1>
        <p className="mt-6 text-lg max-w-[52ch] text-[var(--line)]">{t("heroBody")}</p>
        <div className="flex flex-wrap gap-3 mt-10">
          <CtaLink to="book" testid="hero-book">{t("heroCta1")}</CtaLink>
          <CtaLink to="oracle" variant="ghost" testid="hero-oracle">{t("heroCta2")}</CtaLink>
        </div>
        <p className="text-sm mt-6 text-[var(--gold-light)]">{t("heroSmall")}</p>
      </div>
      <p className="absolute bottom-3 right-4 text-[11px] opacity-60">{t("heroNote")}</p>
    </section>
  );
}

function Paths() {
  const { t, tl, lp } = useT();
  const p = tl("paths");
  const items = [[p[0], p[1], p[2], "/sessions", "/assets/v2/andreea-studio.webp"], [p[3], p[4], p[5], "/love-oracle", "/assets/v2/andreea-tarot.webp"], [p[6], p[7], p[8], "/oracle", "/assets/v2/oracle-andreea-waterfall.webp"]];
  return (
    <section className="section">
      <div className="wrap">
        <h2 className="max-w-[20ch]">{t("pathsH")}</h2>
        <p className="lead mt-4">{t("pathsIntro")}</p>
        <div className="grid md:grid-cols-3 gap-8 mt-14">
          {items.map(([title, body, cta, to, img], k) => (
            <HaloCard key={k} className={`card overflow-hidden ${k === 1 ? "md:translate-y-10" : ""}`} testid={`path-card-${k}`}>
              <Link to={lp(to)} className="block">
                <img src={img} alt="" aria-hidden="true" loading="lazy" className="w-full h-56 object-cover" />
                <div className="p-7"><h3>{title}</h3><p className="text-[var(--muted)] mt-3 text-[16px]">{body}</p><span className="inline-block mt-5 font-semibold border-b border-[var(--gold)]">{cta}</span></div>
              </Link>
            </HaloCard>
          ))}
        </div>
      </div>
    </section>
  );
}

// React does not reflect `muted` to the DOM, which blocks autoplay on Safari/mobile; set it imperatively.
function BgVideo({ className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    v.muted = true; v.defaultMuted = true; v.setAttribute("muted", ""); v.setAttribute("playsinline", "");
    const tryPlay = () => { v.play().catch(() => {}); };
    tryPlay();
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) tryPlay(); else v.pause(); }, { threshold: 0.1 });
    io.observe(v);
    const onTouch = () => tryPlay();
    window.addEventListener("touchstart", onTouch, { once: true, passive: true });
    window.addEventListener("click", onTouch, { once: true });
    return () => { io.disconnect(); window.removeEventListener("touchstart", onTouch); window.removeEventListener("click", onTouch); };
  }, []);
  return (
    <video ref={ref} className={`object-cover ${className}`} autoPlay muted loop playsInline preload="auto"
      poster="/assets/v2/green-poster.webp" aria-hidden="true" data-testid="how-bg-video" src="/assets/v2/green-loop.mp4" />
  );
}

function SessionsPreview() {
  const { t, lp, pick, locale } = useT();
  const [svcs, setSvcs] = useState([]);
  useEffect(() => { pub.get("/public/services").then((r) => setSvcs(r.data)).catch(() => {}); }, []);
  return (
    <section className="relative overflow-hidden dark-zone bg-[var(--forest-deep)] text-[var(--ivory)] lg:flex lg:flex-col lg:min-h-[max(640px,min(56.25vw,1000px))]" data-testid="how-video-section">
      <BgVideo className="relative block w-full aspect-video lg:absolute lg:inset-0 lg:h-full lg:aspect-auto" />
      <div className="hidden lg:block absolute inset-0 bg-[linear-gradient(90deg,rgba(7,22,18,0.82)_0%,rgba(7,22,18,0.4)_36%,transparent_52%),linear-gradient(0deg,rgba(7,22,18,0.9)_0%,rgba(7,22,18,0.35)_22%,transparent_40%)]" aria-hidden="true" />
      <div className="wrap relative lg:flex-1 flex flex-col justify-between gap-10 !py-12 lg:!py-16">
        <div className="lg:max-w-[400px]" data-testid="how-video-text"><Eyebrow>{t("howH")}</Eyebrow><h2>{t("sessionsH1")}</h2><p className="lead mt-4 !text-[var(--ivory)] opacity-90">{t("sessionsLead")}</p>
          <p className="notice mt-6 text-sm !bg-[rgba(255,252,246,0.94)] !text-[#183A32]">{t("previewPrice")}</p></div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,300px)_minmax(0,300px)_auto] lg:items-center" data-testid="how-video-actions">
          {svcs.map((s) => (
            <Link key={s.id} to={lp(`/book?service=${s.id}`)} className="card p-5 flex items-center justify-between gap-4 hover:border-[var(--gold)] transition-colors text-[var(--ink)]" data-testid={`home-service-${s.id}`}>
              <div className="min-w-0"><h3 className="text-xl sm:text-2xl">{pick(s.title_i18n)}</h3><p className="text-sm text-[var(--muted)]">{s.duration_min} {t("minutes")}</p></div>
              <span className="serif text-2xl sm:text-3xl whitespace-nowrap shrink-0">{fmtMoney(s.price_minor, s.currency, locale)}</span>
            </Link>
          ))}
          <CtaLink to={lp("/sessions")} variant="forest" className="justify-self-start" testid="how-video-choose-time">{t("chooseTime")}</CtaLink>
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const { t, pick } = useT();
  const cfg = useConfig();
  const [data, setData] = useState({ items: [], demo_fixtures: [] });
  useEffect(() => { pub.get(`/public/reviews?preview=${cfg.env === "preview"}`).then((r) => setData(r.data)).catch(() => {}); }, [cfg.env]);
  const list = data.items.length ? data.items : data.demo_fixtures.slice(0, 3);
  return (
    <section className="section" data-testid="reviews-section">
      <div className="wrap">
        <h2>{t("reviewsH")}</h2>
        {!data.items.length && <p className="lead mt-4" data-testid="reviews-empty">{t("reviewsEmpty")}</p>}
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {list.map((r) => (
            <figure key={r.id} className="card p-6" data-testid="review-card">
              {r.demo && <p className="text-[11px] font-bold tracking-wider text-[var(--danger)] mb-3">{t("demoLabel")}</p>}
              <blockquote className="serif text-2xl">“{pick(r.text)}”</blockquote>
              <figcaption className="text-sm text-[var(--muted)] mt-4">{r.alias}{r.country_optional ? ` · ${r.country_optional}` : ""}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function LunarStrip() {
  const { t, tl, pick, lp } = useT();
  const [d, setD] = useState(null);
  useEffect(() => { pub.get(`/public/calendar?timezone=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`).then((r) => setD(r.data)).catch(() => {}); }, []);
  const phases = tl("phases");
  const keys = ["new_moon", "waxing_crescent", "first_quarter", "waxing_gibbous", "full_moon", "waning_gibbous", "last_quarter", "waning_crescent"];
  return (
    <section className="dark-zone relative overflow-hidden bg-[#0a0b1c] text-[var(--ivory)] pt-16 md:py-24 flex flex-col md:block" data-testid="daily-astro">
      <img src="/assets/v2/astrologic.webp" alt="" aria-hidden="true" loading="lazy" className="order-last mt-8 w-full h-auto md:mt-0 md:absolute md:inset-0 md:h-full md:object-cover" />
      <div className="wrap relative grid justify-items-center text-center gap-6 md:max-w-[760px]">
        <div><p className="eyebrow">{t("dailyLabel")}</p><p className="serif text-3xl md:text-4xl" data-testid="home-daily">{d ? pick(d.daily.text) : "…"}</p></div>
        <div className="text-sm">{d?.astronomy ? <span>{tl("calLabels")[1]}: <strong>{phases[keys.indexOf(d.astronomy.phase)]}</strong> · {d.astronomy.illumination_pct}%</span> : null}
          <Link to={lp("/calendar")} className="block mt-2 underline underline-offset-4">{tl("nav")[7]} →</Link></div>
      </div>
    </section>
  );
}

export default function Home() {
  const { t, tl, lp } = useT();
  useSeo("home");
  const [turn, quiz] = tl("freeCtas");
  return (
    <>
      <Hero />
      <Paths />
      <section className="section">
        <div className="wrap grid md:grid-cols-[0.9fr_1.1fr] gap-14 items-center">
          <img src="/assets/v2/andreea-welcome-600.webp" srcSet="/assets/v2/andreea-welcome-600.webp 600w, /assets/v2/andreea-welcome-920.webp 920w" sizes="(min-width: 768px) 460px, 90vw" alt="Visionary portrait of Andreea with angel wings under a full moon" width="600" height="750" data-testid="welcome-portrait" loading="lazy" className="w-full max-w-[460px] rounded-t-[240px] rounded-b-3xl object-cover object-top aspect-[4/5]" />
          <div><h2>{t("introH")}</h2><p className="mt-6 text-lg prose-max">{t("introBody")}</p><p className="serif text-2xl mt-6">{t("signoff")}</p>
            <CtaLink to={lp("/about")} variant="forest" className="mt-8" testid="home-meet">{t("introCta")}</CtaLink></div>
        </div>
      </section>
      <SessionsPreview />
      <section className="section">
        <div className="wrap grid md:grid-cols-2 gap-14 items-center">
          <div className="order-2 md:order-1"><Eyebrow>{t("oracleEyebrow")}</Eyebrow><h2>{t("oracleH")}</h2><p className="lead mt-4">{t("oracleBody")}</p>
            <CtaLink to={lp("/oracle")} className="mt-8" testid="home-oracle">{t("oracleCta")}</CtaLink></div>
          <HaloCard className="order-1 md:order-2 card p-6"><Link to={lp("/oracle")} data-testid="home-oracle-image" aria-label="Global Ascension Oracle"><img src="/assets/v2/oracle-andreea-reading.webp" alt="Andreea with the Global Ascension Oracle box" loading="lazy" className="w-full rounded-2xl aspect-square object-cover" /></Link></HaloCard>
        </div>
      </section>
      <section className="section bg-violet-wash">
        <div className="wrap text-center grid justify-items-center"><h2>{t("freeH")}</h2><p className="lead mt-4">{t("freeBody")}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8"><CtaLink to={lp("/love-tarot")} variant="forest" testid="home-tarot">{turn}</CtaLink><CtaLink to={lp("/love-oracle")} variant="ghost" testid="home-quiz">{quiz}</CtaLink></div>
          <ThreeCards /></div>
      </section>
      <ReviewsWall />
      <WeeklyPost />
      <YouTubeHub />
      <Donate compact />
      <section className="section bg-[var(--paper)]">
        <div className="wrap grid md:grid-cols-3 gap-6">
          <ClubCards />
          <div className="md:col-span-3"><CtaLink to={lp("/club")} variant="ghost">{tl("nav")[9]} →</CtaLink></div>
        </div>
      </section>
      <section className="section relative overflow-hidden">
        <div className="wrap grid md:grid-cols-2 gap-12 items-center">
          <img src="/assets/v2/andreea-ritual.webp" alt="" aria-hidden="true" loading="lazy" className="rounded-3xl w-full object-cover aspect-[4/3]" />
          <div><h2>{t("peruH1")}</h2><p className="lead mt-4">{t("peruIntro")}</p><CtaLink to={lp("/peru")} variant="forest" className="mt-8" testid="home-peru">{t("peruCta")}</CtaLink></div>
        </div>
      </section>
      <section className="section bg-[var(--paper)] overflow-hidden"><div className="wrap grid lg:grid-cols-[0.8fr_1.2fr] gap-12 items-center">
        <div><h2>{t("videoH")}</h2><p className="lead mt-4">{t("videoBody")}</p><p className="mt-4 text-sm">{t("liveEmpty")}</p><CtaLink to={lp("/library")} variant="ghost" className="mt-6">{t("videoCta")}</CtaLink></div>
        <Mosaic /></div></section>
      <LunarStrip />
      <section className="section"><div className="wrap grid md:grid-cols-2 gap-10 items-center">
        <div><SparkPortrait /><h2 className="mt-6">{t("closingH")}</h2><p className="lead mt-4">{t("closingBody")}</p><CtaLink to={lp("/book")} className="mt-8" testid="home-closing-book">{t("closingCta")}</CtaLink></div>
        <div className="card p-8"><h3>{t("newsletterH")}</h3><p className="text-sm text-[var(--muted)] mt-3">{t("newsletterBody")}</p><CtaLink to={lp("/account/notifications")} variant="ghost" className="mt-5" testid="home-newsletter">{t("clubCta")}</CtaLink></div>
      </div></section>
    </>
  );
}
